import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRightLeft, Check, X, Clock, Package, History,
} from "lucide-react";

export default function AdminSupplies() {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/supplies/transfers/admin-history");
        setTransfers(res.data || []);
      } catch {
        toast.error("Error al cargar historial de traslados");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const statusBadge = (status) => {
    if (status === "pending") return <Badge className="bg-amber-100 text-amber-800 border-amber-300"><Clock className="w-3 h-3 mr-1" />Pendiente</Badge>;
    if (status === "accepted") return <Badge className="bg-green-100 text-green-800 border-green-300"><Check className="w-3 h-3 mr-1" />Aceptado</Badge>;
    if (status === "rejected") return <Badge className="bg-red-100 text-red-800 border-red-300"><X className="w-3 h-3 mr-1" />Rechazado</Badge>;
    return null;
  };

  return (
    <div data-testid="admin-supplies-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-[#FFD62B]/20 flex items-center justify-center">
          <Package className="w-5 h-5 text-[#FFD62B]" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold">Historial de Traslados de Insumos</h1>
          <p className="text-sm text-muted-foreground">Trazabilidad completa de todos los movimientos entre técnicos</p>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="h-20 rounded-xl bg-secondary animate-pulse" />)}
        </div>
      ) : transfers.length === 0 ? (
        <Card className="p-8 text-center">
          <History className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground">No hay traslados registrados</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {transfers.map(t => (
            <Card key={t.id} className="p-4" data-testid={`admin-transfer-${t.id}`}>
              <div className="flex items-start gap-3">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                  t.status === "accepted" ? "bg-green-100" : t.status === "rejected" ? "bg-red-100" : "bg-amber-100"
                }`}>
                  <ArrowRightLeft className={`w-4 h-4 ${
                    t.status === "accepted" ? "text-green-700" : t.status === "rejected" ? "text-red-700" : "text-amber-700"
                  }`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-sm font-semibold">{t.product_name}</span>
                    <span className="text-sm font-mono">{t.quantity} {t.unit}</span>
                    {statusBadge(t.status)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{t.from_technician_name}</span>
                    <span className="mx-1.5">→</span>
                    <span className="font-medium text-foreground">{t.to_technician_name}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {t.from_origin === "tarea" ? "Tarea" : "Extra"} → {t.to_origin === "tarea" ? "Tarea" : "Extra"}
                    {" · Fecha: "}{t.transfer_date}
                    {t.responded_at && ` · Respondido: ${new Date(t.responded_at).toLocaleDateString("es-SV")}`}
                  </div>
                  {t.notes && <div className="text-xs text-muted-foreground mt-1 italic">"{t.notes}"</div>}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
